import React, { useState } from 'react';
import {
  Home,
  MessageSquare,
  Globe,
  TrendingDown,
  Award,
  Trophy,
  FolderOpen,
  Zap,
  User,
  ShoppingBag,
  Settings,
  FolderKanban,
  KeyRound,
  Users,
  CreditCard,
  ChevronDown,
  Menu,
  X,
  Check,
  Sparkles,
  Presentation,
} from 'lucide-react';
import { useI18n } from '../i18n/useI18n';
import type { WorkspaceItem } from '../db/indexedDB';

interface SidebarProps {
  activeMenu: string;
  onMenuChange: (menu: string) => void;
  workspaces: WorkspaceItem[];
  activeWorkspace: WorkspaceItem;
  onWorkspaceChange: (ws: WorkspaceItem) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeMenu, onMenuChange, workspaces, activeWorkspace, onWorkspaceChange }) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(true);
  const [wsDropdownOpen, setWsDropdownOpen] = useState(false);

  // Shared section label
  const sectionLabel = (text: React.ReactNode) => (
    <div className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
      {text}
    </div>
  );

  // Navigation item — light purple active state
  const menuItem = (id: string, label: string, icon: React.ReactNode) => (
    <button
      onClick={() => onMenuChange(id)}
      aria-current={activeMenu === id ? 'page' : undefined}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-1 ${
        activeMenu === id
          ? 'text-violet-700 bg-violet-50 font-medium'
          : 'text-zinc-500 hover:bg-zinc-100/70 hover:text-zinc-800'
      }`}
    >
      <span aria-hidden="true" className="shrink-0">{icon}</span>
      <span>{label}</span>
    </button>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close sidebar' : 'Open sidebar'}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg  outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
      >
        {isOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
      </button>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-screen w-45 bg-white overflow-y-auto transition-transform duration-200 z-40 lg:relative lg:tranzinc-x-0 ${
          isOpen ? 'tranzinc-x-0' : '-tranzinc-x-full'
        }`}
      >
        {/* Workspace header */}
        <div className="relative px-3 py-3">
          <button
            onClick={() => setWsDropdownOpen(o => !o)}
            className="flex items-center justify-center gap-2 w-full hover:bg-zinc-100/60 rounded-xl px-2 py-1.5 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
          >
            <img
              src={`https://www.google.com/s2/favicons?domain=${activeWorkspace.domain}&sz=32`}
              alt=""
              aria-hidden="true"
              className="w-6 h-6 object-contain shrink-0"
            />
            <div className="flex items-center gap-1 min-w-0">
              <div className="text-left">
                <div className="font-semibold text-[13px] truncate text-zinc-950">{activeWorkspace.name}</div>
                <div className="text-[11px] text-zinc-400 truncate">{activeWorkspace.domain}</div>
              </div>
              <ChevronDown size={13} className={`text-zinc-400 shrink-0 transition-transform duration-150 ${wsDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </div>
          </button>

          {wsDropdownOpen && (
            <div className="absolute left-2 right-2 top-full mt-1 bg-white border border-zinc-200 rounded-xl z-50 py-1.5 shadow-xl shadow-zinc-200/60 animate-slide-up">
              {/* 新建项目 — 暂不可用 */}
              <div
                aria-disabled="true"
                className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg opacity-50 cursor-not-allowed select-none"
                title="即将推出"
              >
                <div className="w-6 h-6 rounded-md border border-dashed border-zinc-300 bg-zinc-50 flex items-center justify-center shrink-0">
                  <span className="text-zinc-400 text-base leading-none">+</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-zinc-500">新建项目</div>
                </div>
                <span className="shrink-0 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400">即将推出</span>
              </div>
              {/* 分割线 */}
              <div className="my-1 border-t border-zinc-100" />
              {workspaces.map(ws => (
                <button
                  key={ws.id}
                  onClick={() => { onWorkspaceChange(ws); setWsDropdownOpen(false); }}
                  className="flex items-center gap-2.5 w-full px-2.5 py-1.5 text-left hover:bg-zinc-50 transition-colors rounded-lg"
                >
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${ws.domain}&sz=32`}
                    alt=""
                    aria-hidden="true"
                    className="w-6 h-6 object-contain shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate text-zinc-900">{ws.name}</div>
                    <div className="text-[11px] text-zinc-400 truncate">{ws.domain}</div>
                  </div>
                  {ws.id === activeWorkspace.id && <Check size={13} className="text-zinc-950 shrink-0" aria-hidden="true" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="px-3 pb-3" aria-label="Main navigation">
          {/* GEO Agent — featured entry */}
          <div className="pt-2 pb-2">
            <button
              onClick={() => onMenuChange('geo-agent')}
              aria-current={activeMenu === 'geo-agent' ? 'page' : undefined}
              className={`relative overflow-hidden w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl text-sm font-semibold transition-all outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 ${
                activeMenu === 'geo-agent'
                  ? 'bg-gradient-to-br from-indigo-600 to-cyan-500 text-white shadow-lg shadow-indigo-300/50'
                  : 'bg-gradient-to-br from-indigo-600 to-cyan-500 text-white shadow-md shadow-indigo-200/40 hover:shadow-lg hover:shadow-indigo-300/50 hover:brightness-105'
              }`}
            >
              {/* shimmer sweep */}
              <span
                aria-hidden="true"
                className="animate-shimmer pointer-events-none absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              />
              <Sparkles size={16} className="animate-sparkle shrink-0" aria-hidden="true" />
              <div className="flex-1 text-left leading-tight">
                <div>GEO Agent</div>
                <div className="text-[10px] font-normal text-white/70">全自动工作流</div>
              </div>
              <Zap size={12} className="shrink-0 text-white/80" aria-hidden="true" />
            </button>
          </div>

          {/* General */}
          {sectionLabel(t('sidebar.general'))}
          {menuItem('overview', t('sidebar.overview'), <Home size={16} />)}
          {menuItem('prompts', t('sidebar.prompts'), <MessageSquare size={16} />)}

          {/* Dashboard */}
          {sectionLabel(t('sidebar.dashboard'))}
          {menuItem('domains', t('sidebar.domains'), <Globe size={16} />)}
          {menuItem('gap-analysis', t('sidebar.gapAnalysis'), <TrendingDown size={16} />)}
          {menuItem('insights', t('sidebar.insights'), <Zap size={16} />)}
          {menuItem('impact', t('sidebar.impact'), <TrendingDown size={16} />)}

          {/* Actions */}
          {sectionLabel(
            <span className="flex items-center gap-1.5">
              {t('sidebar.actions')}
              <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-zinc-400">{t('sidebar.beta')}</span>
            </span>
          )}
          {menuItem('earned', t('sidebar.earned'), <Award size={16} />)}
          {menuItem('owned', t('sidebar.owned'), <Trophy size={16} />)}
          {menuItem('materials', t('sidebar.materials'), <FolderOpen size={16} />)}

          {/* Project */}
          {sectionLabel(t('sidebar.project'))}
          {menuItem('profile', t('sidebar.profile'), <User size={16} />)}
          {menuItem('brands', t('sidebar.brands'), <ShoppingBag size={16} />)}

          {/* Company */}
          {sectionLabel(t('sidebar.company'))}
          {menuItem('settings', t('sidebar.settings'), <Settings size={16} />)}
          {menuItem('projects', t('sidebar.projects'), <FolderKanban size={16} />)}
          {menuItem('api-keys', t('sidebar.apiKeys'), <KeyRound size={16} />)}
          {menuItem('members', t('sidebar.members'), <Users size={16} />)}
          {menuItem('billing', t('sidebar.billing'), <CreditCard size={16} />)}

          {/* Landing page */}
          {sectionLabel('产品介绍')}
          {menuItem('landing', '产品 Landing Page', <Presentation size={16} />)}
        </nav>


      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
